import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";
import { AuthProvider, HttpError } from "react-admin";
import { apiService } from "./apiService";
import { SecondFactor } from "./authentication/SecondFactorLogin";
import createUuid from "./helpers/createUuid";
import getSubscriptionInfo from "./users/subscriptions/getSubscriptionInfo";

type Session = {
  sessionId: string
  userId: string
  username?: string
}

const COOKIENAME = 'Authorization'

const getUserPropsFromToken = (token: string) => {
  return jwtDecode<{sub: string, name: string}>(token)
}

const getSessionFromCookie: () => (Session | undefined) = () => {
  const token = Cookies.get(COOKIENAME)
  if(!token) return undefined
  var separatorIndex = token.indexOf(":")
  if(separatorIndex === -1) return undefined
  const sessionId = token.substring(0, separatorIndex)
  try {
    const userProps = getUserPropsFromToken(token)
    return {
      sessionId: sessionId,
      userId: userProps.sub,
      username: userProps.name
    }
  } catch (error) {
    console.log(error)
    return undefined
  }
}

const isFreeUser = (subscriptions: string[]) => {
  var subscriptionInfo = getSubscriptionInfo(subscriptions)
  return subscriptionInfo?.name === "free"
}

const isFblUser = (subscriptions: string[]) => {
  var subscriptionInfo = getSubscriptionInfo(subscriptions)
  return subscriptionInfo?.isFblUser === true
}

const redirectTo = (subscriptions: string[]) => {
  var subscriptionInfo = getSubscriptionInfo(subscriptions)
  return subscriptionInfo?.redirectToFff === true
}

const fffBaseUrl = process.env.REACT_APP_FREE_FIBU_BASE_URL ? process.env.REACT_APP_FREE_FIBU_BASE_URL : 'http://localhost:10101/fff'

const authProvider: AuthProvider = {
  login: async({ loginType, username, password, secondFactor, activationId, activationKey}:{
    loginType?: "login" | "login2Fa" | "setupUserLogin", 
    username?:string, 
    password?:string, 
    secondFactor?: SecondFactor
    activationId?: string
    activationKey?: string
  }) => {
    try {
      var token: string | undefined | null = undefined
      var sessionId = createUuid()
      switch (loginType) {
        case "login":
          var {data: loginResult} = await apiService.UsersApi.login({loginCommand: {username: username, password: password, sessionId: sessionId}})
          if(loginResult.secondFactor) return Promise.reject({
            reason: "SecondFactorMissing",
            authKey: loginResult.authKey,
            userId: loginResult.userId
          })
          token = loginResult.token
          break
        case "login2Fa":
          var {data: login2FaResult} = await apiService.UsersApi.login2Fa({login2FaCommand: {authKey: secondFactor?.authKey, userId: secondFactor?.userId, secondFactor: secondFactor?.secondFactor, sessionId: sessionId}})
          token = login2FaResult.token
          break
        case "setupUserLogin":
          var {data: setupUserResult} = await apiService.UsersApi.setupUserLogin({setupUserLoginCommand: {activationId: activationId, activationKey: activationKey, username: username, password: password, sessionId: sessionId}})
          token = setupUserResult.token
          break
        default:
          break
      }
      if(!token) {
        return Promise.reject(
          new HttpError(`Login fehlgeschlagen: Kein Token`, 401)
        )
      }
      if(token) {
        Cookies.set('Authorization', `${sessionId}:${token}`)
        apiService.refresh()
        var userProps = getUserPropsFromToken(token)
        var {data: subscriptions} = await apiService.UsersApi.getActiveSubscriptions({id: userProps.sub})
        var redirectToFff = redirectTo(subscriptions)
        document.title = redirectToFff ? "freeFiBU" : "freeBill"
        return Promise.resolve({redirectTo: redirectToFff ? `${fffBaseUrl}/status` : undefined})
      }
    } catch (error: any) {
      return Promise.reject(
        new HttpError(`Login fehlgeschlagen: ${error.response.data}`, 401)
      )
    }
  },
  checkError: (error: any) => {
    const token = Cookies.get(COOKIENAME)
    const status = error?.status
    if (!token || (token && status === 401) || (token && status === 403)) {
      return Promise.reject()
    }
    return Promise.resolve()
  },
  checkAuth: async() => {
    var session = getSessionFromCookie()
    if(!session) {
      Cookies.remove(COOKIENAME)
      return Promise.reject()
    }
    try {
      var {data:isAuthenticated} = await apiService.UsersApi.isUserAuthenticated({id: session.userId, sessionId: session.sessionId})
      if(!isAuthenticated) Cookies.remove(COOKIENAME)
      return isAuthenticated 
        ? Promise.resolve() 
        : Promise.reject()
    } catch (error) {
      return Promise.reject()
    }
  },
  logout: async() => {
    var session = getSessionFromCookie()
    if(!session) {
      Cookies.remove(COOKIENAME)
      return Promise.resolve()
    } 
    await apiService.UsersApi.logout({logoutCommand: {userId: session.userId, sessionId: session.sessionId}})
    Cookies.remove(COOKIENAME)
    return Promise.resolve()  
  },
  getIdentity: () => {
    var session = getSessionFromCookie()
    if(!session) {
      Cookies.remove(COOKIENAME)
      return Promise.reject()
    } 
    return Promise.resolve({id: session.userId, fullName: session.username})
  },
  getPermissions: async() => {
    var session = getSessionFromCookie()
    if(!session) {
      Cookies.remove(COOKIENAME)
      return Promise.reject()
    } 
    try {
      var {data: subscriptions} = await apiService.UsersApi.getActiveSubscriptions({id: session.userId})
      var {data: totalInvoiceCount} = await apiService.InvoicesApi.getTotalInvoiceCountForMontAndYear({userId: session.userId})
      var freeUser = isFreeUser(subscriptions)
      var fblUser = isFblUser(subscriptions)
      var redirectToFff = redirectTo(subscriptions)
      var createInvoice = (freeUser && totalInvoiceCount < 3) || !freeUser
      return {id: session.userId, freeUser: freeUser, createInvoice: createInvoice, fffUser: !fblUser, activeSubscriptions: subscriptions, redirectToFff: redirectToFff}  
    } catch (error) {
      return Promise.reject()
    }
  },
};

export default authProvider